<template>
  <section
    class="pass-history-view v3-pass-history behaviour-upload-log-tab"
    :aria-label="behaviourTerminology.uploadLogTab"
    data-testid="behaviour-upload-log-tab"
  >
    <h2 class="sr-only">{{ behaviourTerminology.uploadLogTab }}</h2>

    <div
      class="d-flex flex-wrap align-items-center justify-content-between gap-3 mb-3"
    >
      <span class="behaviour-point-history-date-range-text small">
        {{ displayDateRange }}
      </span>
    </div>

    <ActionPanel class="mb-3 behaviour-point-history-action-panel">
      <template #left-content>
        <div class="behaviour-point-history-filters w-100 min-w-0">
          <div class="behaviour-point-history-date-range-row mb-3">
            <CustomSelectDatePicker
              v-model="state.filterParams.dates"
              class="behaviour-point-history-date-range-picker"
              :date-ranges="optionalDateRanges"
              :selected-school-year-limit="schoolYearForDatePicker"
              :should-persist-value="true"
              input-id="behaviour-upload-log-date-range"
              :accessible-label="$t('common.date_range')"
              :placeholder="$t('common.select')"
              data-testid="behaviour-upload-log-date-range"
              @update:model-value="onDateRangeChange"
            />
          </div>
          <div
            class="d-flex flex-wrap align-items-end gap-3 behaviour-point-history-toolbar"
          >
            <div
              class="behaviour-point-history-search-field min-w-0 flex-grow-1"
            >
              <label
                id="behaviour-upload-log-search-label"
                class="v3-form-label d-block mb-1"
                for="behaviour-upload-log-search"
              >
                {{ $t("common.search") }}
              </label>
              <InputField
                class="behaviour-point-history-search"
                prepend-icon="ri-search-line"
                :model-value="state.searchInput"
                :placeholder="$t('common.search')"
                input-type="search"
                id="behaviour-upload-log-search"
                data-testid="behaviour-upload-log-search"
                @update:model-value="onSearchInput"
              />
            </div>
          </div>
        </div>
      </template>
      <template #right-content>
        <div
          class="d-flex align-items-end flex-wrap justify-content-end gap-2 behaviour-point-history-filter-actions"
        >
          <button
            type="button"
            class="btn btn-link d-flex align-items-center p-0 text-primary text-decoration-none"
            :disabled="!hasActiveFilters || state.isLoading"
            data-testid="behaviour-upload-log-reset-filters"
            @click="resetFilters"
          >
            <i class="ri-filter-off-line" aria-hidden="true" />
            <span class="ms-1 text-decoration-underline">{{
              $t("behaviour_settings.reset_filters")
            }}</span>
          </button>
        </div>
      </template>
    </ActionPanel>

    <Loader :is-processing="state.isLoading" />

    <div
      v-if="state.pagination.total > 0"
      class="d-flex justify-content-end align-items-center mb-2 behaviour-upload-log-per-page"
    >
      <PerPage
        :pagination="paginationDisplay"
        :showing-entries="true"
        select-input-id="behaviour-upload-log-per-page"
        data-testid="behaviour-upload-log-per-page"
        @on-page-change="setPerPage"
      />
    </div>

    <DataTable
      class="datatable bg-white text-center mt-3"
      add-table-classes="behaviour-upload-log-datatable"
      :fields="tableFields"
      :items="state.tableRows"
      :no-items-view="noItemsView"
      :keyboard-scrollable-region="true"
      :scroll-region-label="
        $t('behaviour_manage.table_scroll_region', {
          name: behaviourTerminology.uploadLogTab
        })
      "
      data-testid="behaviour-upload-log-table"
    >
      <template #fileName="{ item }">
        <td class="text-start">{{ item.fileName || "—" }}</td>
      </template>
      <template #module="{ item }">
        <td class="text-start">{{ moduleLabel(item.module) }}</td>
      </template>
      <template #dateTime="{ item }">
        <td class="text-start">{{ formatDateTime(item.dateTime) }}</td>
      </template>
      <template #status="{ item }">
        <td class="text-start">
          <div class="behaviour-upload-log-status-cell">
            <span
              class="behaviour-upload-log-status-pill"
              :class="`behaviour-upload-log-status-pill--${item.status || 'unknown'}`"
            >
              {{ statusLabel(item.status) }}
            </span>
            <button
              v-if="canDownloadErrorLog(item)"
              type="button"
              class="btn btn-link p-0 behaviour-upload-log-error-download"
              :aria-label="
                $t('behaviour_manage.download_error_log_aria', {
                  fileName: item.fileName
                })
              "
              :data-testid="`behaviour-upload-log-error-download-${item.id}`"
              @click="downloadErrorLog(item)"
            >
              <i class="ri-download-2-line" aria-hidden="true" />
            </button>
          </div>
        </td>
      </template>
    </DataTable>

    <div
      v-if="state.isLazyLoadingMode && canLoadMoreLazy"
      ref="lazySentinelRef"
      class="behaviour-core-value-lazy-sentinel py-2 text-center text-muted small"
      data-testid="behaviour-upload-log-lazy-sentinel"
      aria-hidden="true"
    >
      {{ $t("behaviour_settings.lazy_scroll_hint") }}
    </div>

    <Pagination
      v-if="paginationDisplay.pages > 1 && !state.isLazyLoadingMode"
      :active-page="state.pagination.activePage"
      :pages="paginationDisplay.pages"
      class="cs-pagination mt-4"
      :dots="false"
      :double-arrows="true"
      size="lg"
      align="center"
      data-testid="behaviour-upload-log-pagination"
      @update:active-page="setActivePage"
    />
  </section>
</template>

<script>
import { useBehaviourTableLazyScroll } from "@/composables/useBehaviourTableLazyScroll.js"
import { useGradeYears } from "@/composables/useGradeYears.js"
import behaviourFileUploadsConstants from "@/constants/behaviourFileUploadsConstants.js"
import {
  buildBehaviourUploadErrorLogFileName,
  formatBehaviourUploadErrorLogAsText,
  isBehaviourUploadFailedStatus,
  resolveBehaviourUploadModuleLabel
} from "@/helpers/behaviourFileUploadsApi.js"
import {
  buildBehaviourPointHistoryDateRanges,
  getDefaultBehaviourPointHistoryDateRange,
  parseBehaviourPointHistoryDateRange
} from "@/helpers/behaviourPointHistoryFilters.js"
import { formatPointHistoryApiDate } from "@/helpers/behaviourPointHistoryApi.js"
import {
  applyBehaviourTableFetchResult,
  resolveBehaviourTableLazyRequest
} from "@/helpers/behaviourTableLazyLoading.js"
import download from "@/helpers/downloadCSV.js"
import helpers from "@/helpers/index.js"
import ActionPanel from "@/v3components/shared/DataTable/ActionPanel.vue"
import DataTable from "@/v3components/shared/DataTable/DataTable.vue"
import Pagination from "@/v3components/shared/DataTable/Pagination.vue"
import PerPage from "@/v3components/shared/DataTable/Perpage.vue"
import CustomSelectDatePicker from "@/v3components/shared/Form/CustomSelectDatePicker.vue"
import InputField from "@/v3components/shared/Form/InputField.vue"
import Loader from "@/v3components/shared/Loader/Loader.vue"
import { registerPbisCsvUploadRefreshHandler } from "@/helpers/behaviourPbisSocket.js"
import { computed, nextTick, onMounted, onUnmounted, reactive } from "vue"
import { useI18n } from "vue-i18n"
import { useStore } from "vuex"

const { UPLOAD_STATUS } = behaviourFileUploadsConstants

export default {
  name: "BehaviourUploadLogTab",
  components: {
    ActionPanel,
    CustomSelectDatePicker,
    DataTable,
    InputField,
    Loader,
    Pagination,
    PerPage
  },
  setup() {
    const SEARCH_DEBOUNCE_MS = 320
    const { t } = useI18n()
    const store = useStore()
    let searchDebounceId = null
    let unsubscribeCsvUploadRefresh = null

    const behaviourTerminology = computed(
      () => store.getters["behaviourSettings/behaviourTerminology"]
    )

    const { gradeYears, currentSchoolYear, getMinDate, getMaxDate } =
      useGradeYears()

    const schoolYearForDatePicker = computed(
      () => currentSchoolYear.value || gradeYears.value?.[0] || null
    )

    const optionalDateRanges = computed(() =>
      buildBehaviourPointHistoryDateRanges(
        schoolYearForDatePicker.value,
        getMinDate,
        getMaxDate
      )
    )

    const defaultDateRange = computed(() =>
      getDefaultBehaviourPointHistoryDateRange(
        schoolYearForDatePicker.value,
        getMinDate,
        getMaxDate
      )
    )

    const state = reactive({
      isLoading: false,
      isLazyAppendInFlight: false,
      isLazyLoadingMode: false,
      lazyLoadedCount: 0,
      searchInput: "",
      tableRows: [],
      filterParams: {
        dates: ""
      },
      pagination: {
        activePage: 1,
        total: 0,
        pages: 0,
        from: 0,
        to: 0,
        per_page: { label: "25", value: 25 }
      }
    })

    const activeDateRange = computed(() =>
      parseBehaviourPointHistoryDateRange(state.filterParams.dates)
    )

    const displayDateRange = computed(() => {
      const { start, end } = activeDateRange.value
      if (!start || !end) return ""
      if (start.isSame(end, "day")) {
        return start.format("MMM D, YYYY")
      }
      return `${start.format("MMM D, YYYY")} - ${end.format("MMM D, YYYY")}`
    })

    const tableFields = computed(() => [
      {
        key: "fileName",
        label: t("behaviour_manage.col_file_name"),
        sorter: false,
        _style: "width:34%;"
      },
      {
        key: "module",
        label: t("behaviour_manage.col_module"),
        sorter: false,
        _style: "width:22%;"
      },
      {
        key: "dateTime",
        label: t("behaviour_manage.col_date_time"),
        sorter: false,
        _style: "width:22%;"
      },
      {
        key: "status",
        label: t("behaviour_manage.col_status"),
        sorter: false,
        _style: "width:22%;"
      }
    ])

    const paginationDisplay = computed(() => state.pagination)

    const hasActiveFilters = computed(() => {
      const hasSearch = Boolean(String(state.searchInput ?? "").trim())
      const hasDateRange =
        String(state.filterParams.dates ?? "").trim() !==
        String(defaultDateRange.value ?? "").trim()
      return hasSearch || hasDateRange
    })

    const noItemsView = computed(() => ({
      noItems: t("behaviour_manage.no_upload_log_rows")
    }))

    const statusLabel = (status) => {
      if (status === UPLOAD_STATUS.PROCESSING) {
        return t("behaviour_manage.upload_status_processing")
      }
      if (status === UPLOAD_STATUS.PROCESSED) {
        return t("behaviour_manage.upload_status_processed")
      }
      if (status === UPLOAD_STATUS.FAILED) {
        return t("behaviour_manage.upload_status_failed")
      }
      return status || "—"
    }

    const moduleLabel = (moduleValue) =>
      resolveBehaviourUploadModuleLabel(moduleValue, t, {
        point: behaviourTerminology.value?.point
      })

    const formatDateTime = (value) => {
      if (!value) return "—"
      const formatted = helpers.transformUTC?.(value, "MM-DD-YYYY, hh:mm A")
      if (formatted) return formatted
      return (
        helpers.transformDate?.(value, "MM-DD-YYYY, hh:mm A") || String(value)
      )
    }

    const canDownloadErrorLog = (item) =>
      isBehaviourUploadFailedStatus(item?.status)

    const downloadErrorLog = (item) => {
      const text = formatBehaviourUploadErrorLogAsText(item?.errorLog) || "{}"
      download.CSVExport(
        text,
        buildBehaviourUploadErrorLogFileName(item?.fileName),
        ".txt"
      )
    }

    const fetchUploadLogPage = async ({ page, append = false } = {}) => {
      const perPageVal = Number(state.pagination.per_page?.value) || 25
      const perPageLabel =
        state.pagination.per_page?.label || String(perPageVal)
      const { isLazyMode, requestPerPage } =
        resolveBehaviourTableLazyRequest(perPageVal)
      const requestPage = page ?? state.pagination.activePage ?? 1
      const range = activeDateRange.value

      const { rows, pagination } = await store.dispatch(
        "behaviourManage/fetchFileUploads",
        {
          page: requestPage,
          perPage: requestPerPage,
          search: state.searchInput,
          fromDate: formatPointHistoryApiDate(range.start),
          toDate: formatPointHistoryApiDate(range.end)
        }
      )

      applyBehaviourTableFetchResult({
        state,
        rows: rows || [],
        pagination,
        perPageLabel,
        perPageVal,
        isLazyMode,
        append,
        rowsKey: "tableRows"
      })

      return { rows: rows || [], isLazyMode }
    }

    const fetchUploadLog = async ({ append = false } = {}) => {
      try {
        if (append) {
          state.isLazyAppendInFlight = true
        } else {
          state.isLoading = true
        }

        const requestPage = append
          ? (Number(state.pagination.activePage) || 1) + 1
          : Number(state.pagination.activePage) || 1

        const { isLazyMode } = await fetchUploadLogPage({
          page: requestPage,
          append
        })

        if (append && isLazyMode) {
          state.pagination.activePage = requestPage
        }
      } catch {
        if (!append) {
          state.tableRows = []
          state.pagination = {
            ...state.pagination,
            total: 0,
            pages: 0,
            from: 0,
            to: 0
          }
        }
      } finally {
        state.isLoading = false
        state.isLazyAppendInFlight = false
        await nextTick()
        setupLazyObserver()
      }
    }

    const {
      lazySentinelRef,
      canLoadMoreLazy,
      setupLazyObserver,
      teardownLazyObserver
    } = useBehaviourTableLazyScroll({
      state,
      getVisibleCount: () => state.tableRows.length,
      onAppend: () => fetchUploadLog({ append: true })
    })

    const onSearchInput = (value) => {
      state.searchInput = value || ""
      if (searchDebounceId) {
        clearTimeout(searchDebounceId)
      }
      searchDebounceId = setTimeout(() => {
        state.pagination.activePage = 1
        fetchUploadLog()
      }, SEARCH_DEBOUNCE_MS)
    }

    const onDateRangeChange = () => {
      state.pagination.activePage = 1
      fetchUploadLog()
    }

    const resetFilters = () => {
      state.searchInput = ""
      state.filterParams.dates = defaultDateRange.value
      state.pagination.activePage = 1
      fetchUploadLog()
    }

    const setActivePage = (page) => {
      state.pagination.activePage = page
      fetchUploadLog()
      helpers.scrollToTop?.()
    }

    const setPerPage = (option) => {
      if (!option) return
      state.pagination.per_page = option
      state.pagination.activePage = 1
      fetchUploadLog()
    }

    onMounted(() => {
      if (!state.filterParams.dates) {
        state.filterParams.dates = defaultDateRange.value
      }
      fetchUploadLog()
      unsubscribeCsvUploadRefresh = registerPbisCsvUploadRefreshHandler(() => {
        state.pagination.activePage = 1
        fetchUploadLog()
      })
    })

    onUnmounted(() => {
      if (searchDebounceId) {
        clearTimeout(searchDebounceId)
      }
      unsubscribeCsvUploadRefresh?.()
      unsubscribeCsvUploadRefresh = null
      teardownLazyObserver()
    })

    return {
      behaviourTerminology,
      state,
      schoolYearForDatePicker,
      optionalDateRanges,
      displayDateRange,
      tableFields,
      paginationDisplay,
      hasActiveFilters,
      noItemsView,
      lazySentinelRef,
      canLoadMoreLazy,
      statusLabel,
      moduleLabel,
      formatDateTime,
      canDownloadErrorLog,
      downloadErrorLog,
      onSearchInput,
      onDateRangeChange,
      resetFilters,
      setActivePage,
      setPerPage
    }
  }
}
</script>
